import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { WordmarkAltheia } from "@/components/Logo";

/**
 * Sui app shell — collapsible left nav rail + content area.
 *
 * Built against DESIGN.md (Persian + Orange): ghost ground, indigo structure,
 * orange = live/active only (<=10%), coral = denial. Serif-italic titles, mono
 * labels, full borders (no side-stripes). Active route reads as a subtle indigo
 * fill plus a small orange marker dot, never a thick side-stripe.
 *
 * Collapsed state persists in localStorage "altheia.sui.nav". Motion is
 * transform/width-only, ease-out, reduced-motion safe.
 */

const NAV_KEY = "altheia.sui.nav";

export interface SuiAgentRef {
  id: string;
  name: string;
  status?: string;
}

function statusTone(status?: string): "active" | "revoked" | "paused" {
  if (status === "revoked") return "revoked";
  if (status === "paused") return "paused";
  return "active";
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function IconFleet() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
function IconAgents() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 9h6M9 13h6M9 17h3" />
    </svg>
  );
}
function IconChronicle() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M9 9h6M9 13h6" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  );
}
function Chevron() {
  return (
    <svg className="sas__chev-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
function Rail({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: collapsed ? "scaleX(-1)" : "none" }}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="M14.5 9.5L12 12l2.5 2.5" />
    </svg>
  );
}

export function SuiAppShell({ agents, account, children }: { agents: SuiAgentRef[]; account?: ReactNode; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(NAV_KEY) === "1");
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(NAV_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const shown = agents.slice(0, 5);

  return (
    <div className={`sas ${collapsed ? "is-collapsed" : ""}`}>
      <aside className="sas__rail" aria-label="Primary">
        <div className="sas__brand">
          <NavLink to="/sui" className="sas__wordmark" aria-label="Altheia home" end>
            <WordmarkAltheia size={20} />
          </NavLink>
          <span className="sas__chain">sui</span>
        </div>

        <nav className="sas__nav">
          <NavLink
            to="/sui/agents/new"
            className="sas__create"
            title={collapsed ? "Create Agent" : undefined}
          >
            <span className="sas__create-ico"><IconPlus /></span>
            <span className="sas__lbl">Create Agent</span>
          </NavLink>

          <NavLink to="/sui/dashboard" className="sas__row" title={collapsed ? "Fleet" : undefined}>
            <span className="sas__ico"><IconFleet /></span>
            <span className="sas__lbl">Fleet</span>
          </NavLink>

          <div className="sas__group">
            <button
              type="button"
              className={`sas__row sas__row--toggle ${agentsOpen ? "is-open" : ""}`}
              onClick={() => (collapsed ? navigate("/sui/agents") : setAgentsOpen((o) => !o))}
              aria-expanded={agentsOpen}
              title={collapsed ? "Agents" : undefined}
            >
              <span className="sas__ico"><IconAgents /></span>
              <span className="sas__lbl">Agents</span>
              <span className="sas__chev"><Chevron /></span>
            </button>

            {!collapsed && (
              <div className={`sas__sub-wrap ${agentsOpen ? "is-open" : ""}`}>
              <ul className="sas__sub">
                {shown.map((a) => {
                  const tone = statusTone(a.status);
                  return (
                    <li key={a.id}>
                      <NavLink to={`/sui/agents/${a.id}`} className="sas__subitem">
                        <span className={`sas__dot is-${tone}`} aria-hidden="true" />
                        <span className="sas__subname">{a.name}</span>
                      </NavLink>
                    </li>
                  );
                })}
                <li>
                  <NavLink to="/sui/agents" className="sas__subitem sas__more" end>
                    <span className="sas__dot is-ghost" aria-hidden="true" />
                    <span className="sas__subname">more...</span>
                  </NavLink>
                </li>
              </ul>
              </div>
            )}
          </div>

          <NavLink to="/sui/audit" className="sas__row" title={collapsed ? "Chronicle" : undefined}>
            <span className="sas__ico"><IconChronicle /></span>
            <span className="sas__lbl">Chronicle</span>
          </NavLink>

          <NavLink to="/sui/settings" className="sas__row" title={collapsed ? "Settings" : undefined}>
            <span className="sas__ico"><IconSettings /></span>
            <span className="sas__lbl">Settings</span>
          </NavLink>
        </nav>

        <button
          type="button"
          className="sas__collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <span className="sas__ico"><Rail collapsed={collapsed} /></span>
          <span className="sas__lbl">Collapse</span>
        </button>
      </aside>

      <main className="sas__content">
        <header className="sas__topbar">
          <span className="sas__net"><span className="sas__net-dot" /> sui testnet</span>
          <div className="sas__topbar-right">{account}</div>
        </header>
        <div className="sas__page">{children}</div>
      </main>

      <style>{`
        .sas {
          --rail-w: 240px;
          --rail-w-collapsed: 56px;
          display: grid;
          grid-template-columns: var(--rail-w) 1fr;
          min-height: 100vh;
          background: var(--bg);
          font-family: var(--font-body);
          color: var(--bone);
          transition: grid-template-columns 240ms cubic-bezier(0.32, 0.72, 0, 1);
        }
        .sas.is-collapsed { grid-template-columns: var(--rail-w-collapsed) 1fr; }

        .sas__rail {
          display: flex;
          flex-direction: column;
          gap: var(--s-5);
          padding: var(--s-5) var(--s-3);
          border-right: 1px solid var(--line);
          background: var(--bg-elev);
          position: sticky;
          top: 0;
          height: 100vh;
          overflow: hidden;
        }

        .sas__brand {
          display: flex;
          align-items: baseline;
          gap: var(--s-2);
          padding: 0 var(--s-2);
          min-height: 24px;
        }
        .sas__wordmark { font-style: italic; white-space: nowrap; }
        .sas__chain {
          font: var(--type-mono-address);
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: var(--accent-deep);
          opacity: 1;
          transition: opacity var(--dur-snap) ease;
        }
        .is-collapsed .sas__chain { opacity: 0; pointer-events: none; }

        .sas__nav { display: flex; flex-direction: column; gap: var(--s-1); flex: 1; min-height: 0; }

        /* Primary action — stands out: indigo fill, orange plus mark */
        .sas__create {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          height: 40px;
          padding: 0 var(--s-3);
          margin-bottom: var(--s-3);
          background: transparent;
          color: var(--indigo);
          border: 1px solid var(--indigo);
          border-radius: var(--r-sm);
          font: var(--type-label);
          letter-spacing: 0.04em;
          white-space: nowrap;
          transition: background 150ms cubic-bezier(0.23,1,0.32,1), color 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
        }
        .sas__create:hover { background: var(--indigo-tint); color: var(--indigo-deep); }
        .sas__create:active { transform: scale(0.98); }
        .sas__create-ico {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: none;
          width: 22px;
          height: 22px;
          color: var(--accent);
        }

        .sas__group { display: flex; flex-direction: column; }

        .sas__row {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          width: 100%;
          height: 38px;
          padding: 0 var(--s-3);
          border-radius: var(--r-sm);
          color: var(--slate);
          font: var(--type-body);
          white-space: nowrap;
          transition: background 150ms ease, color 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1);
        }
        .sas__row:hover { background: var(--indigo-tint); color: var(--bone); }
        .sas__row:active { transform: scale(0.98); }
        .sas__row.active {
          background: var(--indigo-tint);
          color: var(--bone);
        }
        /* small orange marker for active — not a side-stripe */
        .sas__row.active .sas__ico { color: var(--accent-deep); }
        .sas__row.active .sas__ico::after {
          content: "";
          position: absolute;
          left: -7px;
          top: 50%;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--accent);
          transform: translateY(-50%);
        }
        .sas__ico {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: none;
          width: 22px;
          height: 22px;
          color: inherit;
        }

        .sas__row--toggle .sas__chev {
          margin-left: auto;
          display: inline-flex;
          color: var(--slate-3);
          transition: transform var(--dur-snap) var(--ease-snap), opacity var(--dur-snap) ease;
        }
        .sas__row--toggle.is-open .sas__chev { transform: rotate(90deg); }
        .is-collapsed .sas__chev { opacity: 0; }

        .sas__sub-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 220ms cubic-bezier(0.32, 0.72, 0, 1); }
        .sas__sub-wrap.is-open { grid-template-rows: 1fr; }
        .sas__sub-wrap > .sas__sub { overflow: hidden; }
        .sas__sub {
          list-style: none;
          margin: 2px 0 var(--s-2);
          padding: 0 0 0 22px;
          display: flex;
          flex-direction: column;
          gap: 1px;
          border-left: 1px solid var(--line-soft);
          margin-left: 14px;
        }
        .sas__subitem {
          display: flex;
          align-items: center;
          gap: var(--s-2);
          height: 30px;
          padding: 0 var(--s-2);
          border-radius: var(--r-sm);
          color: var(--slate-2);
          font: var(--type-body-small);
          white-space: nowrap;
          transition: background var(--dur-snap) ease, color var(--dur-snap) ease;
        }
        .sas__subitem:hover { background: var(--indigo-tint); color: var(--bone); }
        .sas__subitem.active { color: var(--bone); font-weight: 500; }
        .sas__subitem.active .sas__dot.is-active { box-shadow: 0 0 0 3px var(--accent-glow); }
        .sas__subname { overflow: hidden; text-overflow: ellipsis; }
        .sas__more { color: var(--slate-3); font: var(--type-mono-address); }

        .sas__dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex: none;
          background: var(--slate-3);
        }
        .sas__dot.is-active { background: var(--accent); animation: ds-pulse var(--dur-pulse) ease-in-out infinite; }
        .sas__dot.is-revoked { background: var(--oxblood); }
        .sas__dot.is-paused { background: var(--accent-deep); }
        .sas__dot.is-ghost { background: transparent; border: 1px solid var(--line-stamp); }

        .sas__collapse {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          height: 36px;
          padding: 0 var(--s-3);
          margin-top: auto;
          border-top: 1px solid var(--line-soft);
          border-radius: 0;
          padding-top: 0;
          color: var(--slate-3);
          font: var(--type-mono-address);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          white-space: nowrap;
          transition: color var(--dur-snap) ease;
        }
        .sas__collapse:hover { color: var(--bone); }

        /* Collapsed: hide labels, center icons */
        .sas__lbl { transition: opacity var(--dur-snap) ease; }
        .is-collapsed .sas__lbl { opacity: 0; width: 0; overflow: hidden; }
        .is-collapsed .sas__create,
        .is-collapsed .sas__row,
        .is-collapsed .sas__collapse { gap: 0; justify-content: center; padding: 0; }
        .is-collapsed .sas__create { justify-content: center; }
        .is-collapsed .sas__brand { justify-content: center; padding: 0; }
        .is-collapsed .sas__wordmark { display: none; }
        .is-collapsed .sas__brand { min-height: 0; }

        .sas__content { min-width: 0; display: flex; flex-direction: column; }
        .sas__topbar { display: flex; align-items: center; justify-content: space-between; height: 56px; padding: 0 var(--s-7); border-bottom: 1px solid var(--line-soft); background: var(--bg); }
        .sas__net { display: inline-flex; align-items: center; gap: 7px; font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.1em; color: var(--slate-3); }
        .sas__net-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); flex: none; }
        .sas__topbar-right { display: flex; align-items: center; gap: var(--s-4); }
        .sas__page { flex: 1; padding: var(--s-6) var(--s-7); min-width: 0; }

        @media (prefers-reduced-motion: reduce) {
          .sas, .sas__create, .sas__row, .sas__subitem, .sas__chev,
          .sas__lbl, .sas__chain, .sas__collapse, .sas__dot.is-active { transition: none !important; animation: none !important; }
        }
      `}</style>
    </div>
  );
}
